import { useCallback, useRef, useState } from "react";

interface CreditSliderProps {
  min: number;
  max: number;
  step: number;
  /** Current amount, in credits. */
  value: number;
  onChange: (credits: number) => void;
  label?: string;
  trackHeight?: number;
  handleSize?: number;
  className?: string;
}

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/**
 * Continuous credit-amount slider, shared by the public pricing calculator and
 * the in-app top-up modal so the two can never drift apart.
 *
 * Buyers pick the exact amount their hiring plan needs, so this is a real range
 * rather than a set of preset bundles — it moves in `step` increments across
 * the whole purchasable range.
 *
 * Interaction rules that matter:
 *  - The whole track is the hit area: a click anywhere jumps to that amount and
 *    a press-and-drag scrubs.
 *  - The handle is one element that moves, never one rendered per stop — that
 *    would unmount and remount on every change and so could only ever jump.
 *  - Fill and handle both derive from the same ratio, so they cannot disagree.
 */
const CreditSlider = ({
  min,
  max,
  step,
  value,
  onChange,
  label = "Amount",
  trackHeight = 27,
  handleSize = 36,
  className = "",
}: CreditSliderProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const span = Math.max(1, max - min);
  const ratio = clamp01((value - min) / span);

  /**
   * A plain `left: n%` would centre the handle on the track's edges at the
   * extremes, so half of it would hang outside the pill. Insetting by half a
   * handle at each end keeps it fully on the track at every value.
   */
  const offset = `calc(${ratio * 100}% + ${handleSize / 2 - ratio * handleSize}px)`;

  const valueFromPointer = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track) return min;
      const rect = track.getBoundingClientRect();
      const travel = rect.width - handleSize;
      if (travel <= 0) return min;
      const r = clamp01((clientX - rect.left - handleSize / 2) / travel);
      const raw = min + r * span;
      return Math.min(max, Math.max(min, Math.round(raw / step) * step));
    },
    [min, max, span, step, handleSize]
  );

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Capture so a drag keeps tracking even when the pointer leaves the track.
    // Never let a capture failure swallow the click — selecting matters more.
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* no capture available; click-to-select still works */
    }
    setIsDragging(true);
    onChange(valueFromPointer(e.clientX));
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    // Only a captured pointer is a drag; a bare hover must not move the handle.
    if (!e.currentTarget.hasPointerCapture?.(e.pointerId)) return;
    onChange(valueFromPointer(e.clientX));
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    try {
      if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch {
      /* already released */
    }
    setIsDragging(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const nudge =
      { ArrowLeft: -step, ArrowDown: -step, ArrowRight: step, ArrowUp: step }[e.key] ??
      { PageDown: -step * 10, PageUp: step * 10 }[e.key];
    const next =
      nudge !== undefined
        ? value + nudge
        : e.key === "Home"
        ? min
        : e.key === "End"
        ? max
        : null;
    if (next === null) return;
    e.preventDefault();
    onChange(Math.min(max, Math.max(min, next)));
  };

  // Suppress the easing while dragging so the handle tracks the pointer 1:1.
  const glide = isDragging ? "" : "transition-[left,width] duration-200 ease-out";

  return (
    <div className={className}>
      {/* The whole pill is the hit area, and is itself the slider. The handle
          overhangs it vertically, so leave room above and below. */}
      <div
        ref={trackRef}
        role="slider"
        tabIndex={0}
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={`${value.toLocaleString()} credits`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={handleKeyDown}
        className="relative rounded-full bg-[#C6E0FF] cursor-pointer touch-none select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#012169] focus-visible:ring-offset-2"
        style={{ height: trackHeight, margin: `${Math.max(0, (handleSize - trackHeight) / 2)}px 0` }}
      >
        {/* Progress fill — ends at the handle's centre, so it shares the offset
            and moves on the same curve. */}
        <div
          className={`absolute left-0 top-0 h-full rounded-full bg-[#012169] ${glide}`}
          style={{ width: offset }}
        />
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#00003F] border-2 border-[#2E54A0] shadow-[0px_2px_6px_rgba(0,0,0,0.2)] ${glide}`}
          style={{ left: offset, width: handleSize, height: handleSize }}
        />
      </div>

      {/* Range ends. The exact amount lives in the credits field, which is
          editable — these only orient the drag. */}
      <div className="flex justify-between mt-3 text-sm font-medium text-[#344054] tabular-nums">
        <span>{min.toLocaleString()}</span>
        <span>{max.toLocaleString()}</span>
      </div>
    </div>
  );
};

export { CreditSlider };
